import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { Database, FileUp, Search, Satellite, ScanSearch } from "lucide-react"
import { useState } from "react"

import { ScenePreview } from "@/components/scene-preview"
import { SceneStatusBadge } from "@/components/status-badge"
import { WorkspacePageHeader } from "@/components/workspace-page-header"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import type { Scene, SceneType } from "@/lib/domain"
import { formatFileSize, formatResolution } from "@/lib/domain"
import { scenesQueryOptions } from "@/lib/query-options"

export const Route = createFileRoute("/admin/imagery")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(
      scenesQueryOptions({
        query: "",
        type: "Any",
        satellite: "Any satellite",
        maxCloudCover: 100,
        maxResolution: 100,
      })
    ),
  head: () => ({
    meta: [{ title: "Imagery catalogue · Service Portal admin" }],
  }),
  component: AdminImageryPage,
})

function AdminImageryPage() {
  const [query, setQuery] = useState("")
  const [type, setType] = useState<"Any" | SceneType>("Any")
  const [selectedScene, setSelectedScene] = useState<Scene | null>(null)
  const [batchMessage, setBatchMessage] = useState("")
  const { data, isFetching } = useQuery(
    scenesQueryOptions({
      query,
      type,
      satellite: "Any satellite",
      maxCloudCover: 100,
      maxResolution: 100,
    })
  )

  return (
    <div className="mx-auto max-w-[1450px]">
      <WorkspacePageHeader
        kicker="Catalogue operations"
        title="Imagery catalogue"
        description="Inspect the representative metadata structure used for optical and SAR inventory. All previews and records are synthetic."
        actions={
          <Button
            type="button"
            onClick={() =>
              setBatchMessage(
                "Demo batch validated: 24 synthetic metadata rows, 0 production files."
              )
            }
          >
            <FileUp />
            Validate demo batch
          </Button>
        }
      />

      {batchMessage ? (
        <p
          className="mt-5 rounded-[12px] border border-[#b8dcc8] bg-[#eaf6ef] px-4 py-3 text-sm text-[#246947]"
          role="status"
        >
          {batchMessage}
        </p>
      ) : null}

      <section className="mt-6 overflow-hidden rounded-[14px] border border-border bg-white">
        <div className="flex flex-col gap-4 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-1 flex-col gap-3 sm:flex-row">
            <div className="relative w-full sm:max-w-[380px]">
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search scene, place, or mission"
                aria-label="Search imagery catalogue"
                className="h-11 rounded-[10px] border-border bg-white pr-9"
              />
              <Search className="pointer-events-none absolute top-3 right-3 size-4 text-muted-foreground" />
            </div>
            <Select
              value={type}
              onValueChange={(value) => setType(value as "Any" | SceneType)}
            >
              <SelectTrigger
                aria-label="Filter imagery by type"
                className="h-11 w-full rounded-[10px] border-border bg-white sm:w-[170px]"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Any">All image types</SelectItem>
                <SelectItem value="OPTICAL">Optical</SelectItem>
                <SelectItem value="SAR">SAR</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Database className="size-4 text-[var(--service-blue)]" />
            {isFetching
              ? "Refreshing…"
              : `${data?.scenes.length ?? 0} representative records`}
          </div>
        </div>

        {data && data.scenes.length > 0 ? (
          <>
            <div className="divide-y divide-border md:hidden">
              {data.scenes.map((scene) => (
                <article key={scene.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <ScenePreview
                      scene={scene}
                      showLabel={false}
                      className="size-16 shrink-0 rounded-[8px]"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-[var(--navy)]">
                            {scene.location}
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {scene.satellite} · {scene.type}
                          </p>
                        </div>
                        <SceneStatusBadge status={scene.status} />
                      </div>
                      <p className="mt-2 truncate font-mono text-[0.6rem] text-muted-foreground">
                        {scene.id}
                      </p>
                    </div>
                  </div>
                  <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 rounded-[10px] bg-[var(--cloud)] p-3">
                    <div>
                      <dt className="text-[0.62rem] text-muted-foreground">
                        Captured
                      </dt>
                      <dd className="mt-1 text-xs font-semibold text-[var(--ink)]">
                        {scene.capturedLabel}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[0.62rem] text-muted-foreground">
                        Resolution
                      </dt>
                      <dd className="mt-1 text-xs font-semibold text-[var(--ink)]">
                        {formatResolution(scene.resolutionMeters)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[0.62rem] text-muted-foreground">
                        Product
                      </dt>
                      <dd className="mt-1 text-xs font-semibold text-[var(--ink)]">
                        {scene.productLevel}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[0.62rem] text-muted-foreground">
                        Demo size
                      </dt>
                      <dd className="mt-1 text-xs font-semibold text-[var(--ink)]">
                        {formatFileSize(scene.fileSizeGb)}
                      </dd>
                    </div>
                  </dl>
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-4 w-full"
                    onClick={() => setSelectedScene(scene)}
                  >
                    <ScanSearch />
                    Inspect scene
                  </Button>
                </article>
              ))}
            </div>

            <div className="hidden overflow-x-auto md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Scene</TableHead>
                    <TableHead>Mission / type</TableHead>
                    <TableHead>Captured</TableHead>
                    <TableHead>Resolution</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.scenes.map((scene) => (
                    <TableRow key={scene.id}>
                      <TableCell className="max-w-[310px]">
                        <div className="flex items-center gap-3">
                          <ScenePreview
                            scene={scene}
                            showLabel={false}
                            className="size-12 shrink-0 rounded-[7px]"
                          />
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-[var(--navy)]">
                              {scene.location}
                            </p>
                            <p className="mt-1 truncate font-mono text-[0.6rem] text-muted-foreground">
                              {scene.id}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        <p className="font-semibold text-[var(--ink)]">
                          {scene.satellite}
                        </p>
                        <p className="mt-1 text-muted-foreground">
                          {scene.type}
                        </p>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {scene.capturedLabel}
                      </TableCell>
                      <TableCell className="text-xs">
                        {formatResolution(scene.resolutionMeters)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {scene.productLevel}
                      </TableCell>
                      <TableCell className="text-xs">
                        {formatFileSize(scene.fileSizeGb)}
                      </TableCell>
                      <TableCell>
                        <SceneStatusBadge status={scene.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedScene(scene)}
                          aria-label={`Inspect ${scene.id}`}
                        >
                          <ScanSearch />
                          Inspect
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        ) : (
          <div className="px-6 py-20 text-center">
            <ScanSearch className="mx-auto size-8 text-[var(--telemetry)]" />
            <h2 className="mt-4 font-semibold text-[var(--navy)]">
              No catalogue records match
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Clear the keyword or choose another image type.
            </p>
          </div>
        )}
      </section>

      <Dialog
        open={selectedScene !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedScene(null)
        }}
      >
        <DialogContent className="max-h-[92svh] overflow-y-auto rounded-[14px] sm:max-w-[760px]">
          {selectedScene ? (
            <>
              <ScenePreview
                scene={selectedScene}
                className="-mx-6 -mt-6 aspect-[16/7] rounded-none"
              />
              <DialogHeader>
                <p className="flex items-center gap-2 text-xs font-semibold text-[var(--service-blue)]">
                  <Satellite className="size-4" />
                  {selectedScene.satellite} · {selectedScene.type}
                </p>
                <DialogTitle className="mt-1 text-2xl font-semibold tracking-[-0.025em] text-[var(--navy)]">
                  {selectedScene.location}
                </DialogTitle>
                <DialogDescription className="font-mono text-[0.65rem]">
                  {selectedScene.id}
                </DialogDescription>
              </DialogHeader>
              <p className="text-sm leading-6 text-muted-foreground">
                {selectedScene.description}
              </p>
              <dl className="grid grid-cols-2 gap-4 rounded-[12px] border border-border bg-[var(--cloud)] p-4 sm:grid-cols-4">
                {[
                  [
                    "Resolution",
                    formatResolution(selectedScene.resolutionMeters),
                  ],
                  ["Bands", selectedScene.band],
                  ["Imaging mode", selectedScene.imagingMode],
                  ["Product level", selectedScene.productLevel],
                  [
                    "Cloud / polarization",
                    selectedScene.cloudCover === null
                      ? selectedScene.polarization
                      : `${selectedScene.cloudCover}%`,
                  ],
                  ["Demo size", formatFileSize(selectedScene.fileSizeGb)],
                  ["Region", selectedScene.region],
                  ["Status", selectedScene.status],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-[0.62rem] text-muted-foreground">
                      {label}
                    </dt>
                    <dd className="mt-1 text-xs font-semibold text-[var(--ink)]">
                      {value}
                    </dd>
                  </div>
                ))}
              </dl>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  )
}
